/* React Error Boundary — catches render/lifecycle errors in any child subtree
   so a single crash never blanks the entire app.

   Usage:
     <ErrorBoundary label="Day Trading">
       <DayTradingPage />
     </ErrorBoundary>

   On error, renders a small "Something went wrong" panel with a Reload button.
   Logs the error + componentStack to console (never throws further). */
import React from "react";

export class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, info) {
    // Silent log — never re-throw.
    try {
      // eslint-disable-next-line no-console
      console.error("[ErrorBoundary]", this.props.label || "unknown", error?.message || error, info?.componentStack);
    } catch {
      /* ignore */
    }
  }

  handleReload = () => {
    try {
      this.setState({ hasError: false, error: null });
    } catch {
      /* ignore */
    }
    // Hard reload as a last resort.
    setTimeout(() => {
      try { window.location.reload(); } catch { /* noop */ }
    }, 50);
  };

  render() {
    if (this.state.hasError) {
      return (
        <div
          data-testid="error-boundary-fallback"
          style={{
            padding: "32px 24px",
            fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
            background: "#0f172a",
            color: "#f1f5f9",
            minHeight: "100vh",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <div
            style={{
              maxWidth: 480,
              background: "#1e293b",
              border: "1px solid #334155",
              borderRadius: 10,
              padding: "28px 32px",
              textAlign: "center",
            }}
          >
            <div style={{ fontSize: 38, marginBottom: 8 }}>⚠</div>
            <h2 style={{ fontSize: 18, fontWeight: 700, marginBottom: 8, letterSpacing: "-0.01em" }}>
              Something went wrong{this.props.label ? ` in ${this.props.label}` : ""}
            </h2>
            <p style={{ fontSize: 13, color: "#94a3b8", marginBottom: 20, lineHeight: 1.55 }}>
              The page hit an unexpected error. Your data is safe — nothing was lost.
              Click below to reload and continue.
            </p>
            {this.state.error && (
              <p
                style={{
                  fontSize: 11,
                  fontFamily: "ui-monospace, SFMono-Regular, monospace",
                  color: "#f87171",
                  background: "#0f172a",
                  padding: "8px 12px",
                  borderRadius: 6,
                  marginBottom: 20,
                  textAlign: "left",
                  overflowX: "auto",
                }}
              >
                {String(this.state.error?.message || this.state.error).slice(0, 240)}
              </p>
            )}
            <button
              data-testid="error-boundary-reload-btn"
              onClick={this.handleReload}
              style={{
                background: "#4f46e5",
                color: "#fff",
                border: "none",
                padding: "10px 22px",
                borderRadius: 7,
                fontSize: 13,
                fontWeight: 600,
                cursor: "pointer",
                letterSpacing: "0.02em",
              }}
            >
              Reload page
            </button>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}

export default ErrorBoundary;
